import React, { useEffect, useState } from 'react';
import {
  List, ListItem, ListItemText, ListItemIcon, IconButton, Switch, Select, MenuItem,
  TextField, Typography, Box, Grid, Chip, CircularProgress, Divider, TableContainer, Table, TableHead, TableBody, TableRow, Avatar
} from '@mui/material';
import PowerIcon from '@mui/icons-material/PowerSettingsNew';
import SaveIcon from '@mui/icons-material/Save';
import SensorsIcon from '@mui/icons-material/Sensors';
import { green, red } from '@mui/material/colors';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import AlertSnackbar from './AlertSnackbar';
import axiosInstance from './axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { Visibility, Edit, Delete } from '@mui/icons-material';
import moment from 'moment';

const SensorStatusPage = () => {
  const { translate } = useLanguage();
  const [sensors, setSensors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const projectId = localStorage.getItem('projectId');

  useEffect(() => {
    fetchSensors();
    fetchRooms();
  }, [projectId]);

  const fetchSensors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/sensors-status`);
      const projectSensors = await axiosInstance.get(`/api/projects/${projectId}/sensors`);
      const sensorsWithStatus = projectSensors.data.map(sensor => {
        const status = response.data.find(s => s.identifier === sensor.identifier);
        return {
          ...sensor,
          isConnected: status ? status.isConnected : false,
          updatedFields: {
            name: sensor.name,
            room_id: sensor.room_id,
            is_registered: sensor.is_registered,
            enabledValues: { ...sensor.enabledValues }
          }
        };
      });
      setSensors(sensorsWithStatus);
    } catch (error) {
      console.error('Error fetching sensors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const updateSensor = async (sensorId, updatedFields) => {
    try {
      await axiosInstance.put(`/api/sensors/${sensorId}/update`, updatedFields);
      fetchSensors();
      showSnackbar('Sensor actualizado correctamente!', 'success');
    } catch (error) {
      showSnackbar('Error al actualizar el sensor', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFieldChange = (sensorId, field, value) => {
    setSensors(prevSensors =>
      prevSensors.map(sensor =>
        sensor.id === sensorId
          ? { ...sensor, updatedFields: { ...sensor.updatedFields, [field]: value } }
          : sensor
      )
    );
  };

  const handleEnabledValueChange = (sensorId, field, value) => {
    setSensors(prevSensors =>
      prevSensors.map(sensor =>
        sensor.id === sensorId
          ? {
              ...sensor,
              updatedFields: {
                ...sensor.updatedFields,
                enabledValues: { ...sensor.updatedFields.enabledValues, [field]: value }
              }
            }
          : sensor
      )
    );
  };

  const handleSave = (sensorId, updatedFields) => {
    updateSensor(sensorId, updatedFields);
  };

  const stats = {
    total: sensors.length,
    connected: sensors.filter(s => s.isConnected).length,
    registered: sensors.filter(s => s.updatedFields.is_registered).length,
    active: sensors.filter(s => s.isConnected && s.updatedFields.is_registered).length
  };

  if (loading) {
    return (
      <ModernLayout title={translate('sensorStatusPage.title')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout title={translate('sensorStatusPage.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('sensorStatusPage.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('sensorStatusPage.subtitle')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('sensorStatusPage.totalSensors')}
            value={stats.total}
            subtitle={translate('sensorStatusPage.online')}
            icon={<SensorsIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('sensorStatusPage.onlineSensors')}
            value={stats.connected}
            subtitle={translate('sensorStatusPage.working')}
            icon={<PowerIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('sensorStatusPage.offlineSensors')}
            value={stats.total - stats.connected}
            subtitle={translate('sensorStatusPage.disconnected')}
            icon={<SensorsIcon />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('sensorStatusPage.maintenance')}
            value={stats.total - stats.active}
            subtitle={translate('sensorStatusPage.requiresAttention')}
            icon={<PowerIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Sensors List */}
      <ModernCard
        title={translate('sensorStatusPage.sensorConfiguration')}
        subtitle={translate('sensorStatusPage.manageSensorConfiguration')}
      >
        <List sx={{ p: 0 }}>
          {sensors.map((sensor, index) => (
            <React.Fragment key={sensor.id}>
              <ListItem 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 3,
                  borderRadius: 2,
                  mb: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateY(-2px)',
                  }
                }}
              >
                <ListItemIcon>
                  <PowerIcon sx={{ 
                    color: sensor.isConnected ? green[500] : red[500],
                    fontSize: 28
                  }} />
                </ListItemIcon>
                
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {sensor.updatedFields.name || sensor.identifier}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Room: {sensor.room_name || 'N/A'}
                    </Typography>
                  </Box>

                  <TextField
                    label="Nombre"
                    variant="outlined"
                    size="small"
                    value={sensor.updatedFields.name}
                    onChange={e => handleFieldChange(sensor.id, 'name', e.target.value)}
                    sx={{ minWidth: 150 }}
                  />

                  <Select
                    value={sensor.updatedFields.room_id || ''}
                    onChange={e => handleFieldChange(sensor.id, 'room_id', e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="" disabled>
                      Seleccionar Room
                    </MenuItem>
                    {rooms.map(room => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Registrado</Typography>
                    <Switch
                      checked={sensor.updatedFields.is_registered}
                      onChange={e => handleFieldChange(sensor.id, 'is_registered', e.target.checked)}
                      color="primary"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label="Temp"
                      size="small"
                      color={sensor.updatedFields.enabledValues?.temperature ? 'success' : 'default'}
                      onClick={() => handleEnabledValueChange(sensor.id, 'temperature', !sensor.updatedFields.enabledValues?.temperature)}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label="Hum"
                      size="small"
                      color={sensor.updatedFields.enabledValues?.humidity ? 'success' : 'default'}
                      onClick={() => handleEnabledValueChange(sensor.id, 'humidity', !sensor.updatedFields.enabledValues?.humidity)}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label="CO2"
                      size="small"
                      color={sensor.updatedFields.enabledValues?.co2 ? 'success' : 'default'}
                      onClick={() => handleEnabledValueChange(sensor.id, 'co2', !sensor.updatedFields.enabledValues?.co2)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>

                  <ModernButton
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSave(sensor.id, sensor.updatedFields)}
                    variant="outlined"
                  >
                    Guardar
                  </ModernButton>
                </Box>
              </ListItem>
              {index < sensors.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </List>
      </ModernCard>

      <AlertSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={handleSnackbarClose}
      />
    </ModernLayout>
  );
};

export default SensorStatusPage;
